<script lang="ts">
export default {
  name: 'KkModal',
}
</script>

<script lang="ts" setup>
import { ref, computed, watch, onUnmounted } from 'vue'

export interface Props {
  isVisible: boolean
  canClickOverlay?: boolean
  width?: string
}

export interface Emits {
  (key: 'change', value: Props['isVisible']): void
}

const props = withDefaults(defineProps<Props>(), {
  canClickOverlay: true,
})
const emits = defineEmits<Emits>()
const offset = ref<number | null>(null)

const containerStyled = computed(() => {
  const styles: Record<string, string> = {}
  if (props.width) styles.width = props.width
  return styles
})

const clickOverlay = () => {
  if (!props.canClickOverlay) return
  emits('change', false)
}

const fixScroll = () => {
  const scrollY = window.scrollY
  document.body.setAttribute('style', `position:fixed; width:100%; top:${ -1 * scrollY }px`)
  offset.value = scrollY
}

const releaseScroll = () => {
  document.body.setAttribute('style', '')
  if (offset.value) {
    window.scrollTo(0, offset.value)
    offset.value = null
  }
}

watch(() => props.isVisible, (value) => {
  if (value) {
    fixScroll()
  } else {
    releaseScroll()
  }
})

onUnmounted(() => {
  releaseScroll()
})
</script>

<template>
  <div class="modal">
    <transition name="container">
      <div v-if="isVisible" class="modal__container" :style="containerStyled">
        <slot />
      </div>
    </transition>

    <transition name="overlay">
      <div v-if="isVisible" class="modal__overlay" @click="clickOverlay" />
    </transition>
  </div>
</template>

<style lang="scss" scoped>
.modal {
  &__container {
    position: fixed;
    top: 50%;
    left: 50%;
    z-index: 1201;
    transform: translate(-50%, -50%);
    width: 1076px;
    max-width: 92vw;
    max-height: 90vh;
    border-radius: 5px;
    overflow: hidden;
    background: #FFF;
    &.container-enter-active,
    &.container-leave-active {
      transition: opacity .5s ease;
    }
    &.container-enter-active {
      transition-delay: .2s;
    }
    &.container-enter-from,
    &.container-leave-to {
      opacity: 0;
    }
  }
  &__header {
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    height: 60px;
    padding: 0 60px;
    color: #FFF;
  }
  &__title {
    font-size: 16px;
    line-height: 1.3;
  }
  &__overlay {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 1200;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, .6);
    content: "";
    &.overlay-enter-active,
    &.overlay-leave-active {
      transition: opacity .5s ease;
    }
    &.overlay-leave-active {
      transition-delay: .2s;
    }
    &.overlay-enter-from,
    &.overlay-leave-to {
      opacity: 0;
    }
  }
  &__content {
    max-height: calc(90vh - 60px);
    padding: 23px 16px 24px;
    overflow-y: auto;
  }
}
</style>
